'use client';

import type {CSSProperties, ReactNode} from 'react';

import styles from './feature-card.module.scss';

/** Tint of the circular badge behind the icon. Figma uses the `*-200` colour ramp. */
export type FeatureCardTone = 'orange' | 'green' | 'red';

interface FeatureCardProps {
    /**
     * Decorative icon element — a `SpotIllustration` or `ExpressiveIcon`. Both render a
     * plain `<img>`, which `.badge img` in the module sizes to 32px, so the caller only
     * picks the glyph.
     */
    icon: ReactNode;
    /** Card heading. */
    title: string;
    /** Supporting copy below the heading. */
    description: string;
    /** Badge tint. */
    tone: FeatureCardTone;
    /**
     * How much wider than the design width to draw the card, so copy that runs longer
     * than the English source fits without making the card taller. 1 is the design width.
     */
    widthScale?: number;
}

/**
 * One value-prop card — Figma nodes 5260:158260 / 5260:158269 / 5260:158278 (desktop)
 * and 5260:160479 / 5260:160488 / 5260:160497 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A white rounded surface with a tinted circular icon badge above centred copy.
 * Presentational and props-only; {@link FeatureCards} owns the copy and the icons.
 * Renders an `<li>`, so it must sit inside the section's list.
 */
export function FeatureCard({icon, title, description, tone, widthScale = 1}: FeatureCardProps) {
    return (
        <li className={styles['card']} style={{'--card-width-scale': widthScale} as CSSProperties}>
            {/* Decorative: the title carries the meaning, and both icon packages render
                their image `aria-hidden` already. */}
            <span className={styles['badge']} data-tone={tone}>
                {icon}
            </span>
            <div className={styles['copy']}>
                <h2 className={styles['title']}>{title}</h2>
                <p className={styles['description']}>{description}</p>
            </div>
        </li>
    );
}
